import type { AnalyticsBrowser } from '@segment/analytics-next';
import { appConfig } from '@/config/app-config';
import type { AccountTraits, ProviderWorkspaceTraits, UserTraits } from './types';
import type { EventName } from './events';

/**
 * Loose track payload — we deliberately do NOT make `track` generic over
 * EventName because TypeScript expands the union at every call site, which
 * triggers TS2590 in JSX-heavy components. Compile-time property validation
 * happens at the `event(...)` factory in events.ts instead.
 *
 * `properties` is `object` (not `Record<string, unknown>`) so structurally
 * typed event property interfaces (without index signatures) satisfy it.
 */
interface TrackPayload {
    name: EventName;
    properties: object;
}

/**
 * Why a custom client wrapper:
 *  - Lazy-load @segment/analytics-next via dynamic import so it lives in a
 *    separate chunk and never blocks the initial bundle.
 *  - Behave as a no-op when SEGMENT_APIKEY is missing/invalid — the app must
 *    keep working identically with or without analytics configured.
 *  - Wrap every call so a failure inside Segment can never throw into the app.
 */

// AnalyticsBrowser is itself thenable (resolves to [Analytics, Context]),
// which would auto-unwrap if returned from a Promise chain. We box it in a
// wrapper object so Promise.then preserves the AnalyticsBrowser instance.
type LoadedSegment = { instance: AnalyticsBrowser } | null;

let cachedInstance: Promise<LoadedSegment> | undefined;

const loadSegment = (): Promise<LoadedSegment> => {
    if (cachedInstance) return cachedInstance;
    const writeKey = appConfig.SEGMENT_APIKEY;
    if (!writeKey) {
        cachedInstance = Promise.resolve(null);
        return cachedInstance;
    }
    cachedInstance = import('@segment/analytics-next')
        .then((mod): LoadedSegment => ({ instance: mod.AnalyticsBrowser.load({ writeKey }) }))
        .catch((): LoadedSegment => null);
    return cachedInstance;
};

const safe = (op: () => void): void => {
    try {
        op();
    } catch {
        // Swallow — analytics must never affect app behavior.
    }
};

export interface AnalyticsClient {
    track(event: TrackPayload): void;
    identify(userId: string, traits?: UserTraits): void;
    groupAccount(accountId: string, traits?: AccountTraits): void;
    groupWorkspace(workspaceKey: string, traits: ProviderWorkspaceTraits): void;
    page(name?: string, properties?: Record<string, unknown>): void;
    reset(): void;
    isEnabled(): boolean;
}

export const createAnalyticsClient = (): AnalyticsClient => {
    const enabled = Boolean(appConfig.SEGMENT_APIKEY);

    const fireAndForget = (op: (a: AnalyticsBrowser) => unknown): void => {
        if (!enabled) return;
        loadSegment()
            .then((loaded) => {
                if (!loaded) return;
                safe(() => {
                    op(loaded.instance);
                });
            })
            .catch(() => {
                // Loader failed — already cached as null on next call.
            });
    };

    return {
        track: (payload) => {
            fireAndForget((a) => a.track(payload.name, payload.properties as Record<string, unknown>));
        },
        identify: (userId, traits) => {
            fireAndForget((a) => a.identify(userId, traits as Record<string, unknown> | undefined));
        },
        groupAccount: (accountId, traits) => {
            fireAndForget((a) => a.group(accountId, traits as Record<string, unknown> | undefined));
        },
        groupWorkspace: (workspaceKey, traits) => {
            // Segment supports a single group call per source; we namespace workspace
            // groups by passing the composite key (e.g. "pagerduty:org_abc123") and tag
            // the traits with provider+workspace_id for downstream filtering.
            fireAndForget((a) => a.group(workspaceKey, traits as unknown as Record<string, unknown>));
        },
        page: (name, properties) => {
            fireAndForget((a) => a.page(name, properties));
        },
        reset: () => {
            fireAndForget((a) => a.reset());
        },
        isEnabled: () => enabled,
    };
};

/**
 * Test-only seam: clear the cached loader so unit tests can re-evaluate
 * SEGMENT_APIKEY between cases.
 */
export const __resetAnalyticsClientForTests = (): void => {
    cachedInstance = undefined;
};
